/**
 * Lobby attract screen: gradient → transparent Utah topo → random pings → LTS / clock / weather.
 * Used as display-kiosk home and as the controller sleep overlay.
 */
import React, { useEffect, useId, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLocationDot } from '@fortawesome/free-solid-svg-icons'
import { WALLBOARD_WEATHER_LOCATIONS } from '../config/wallboardWeather'
import { useWeatherForecastBlocks } from './kioskWeather/useWeatherForecastBlocks'
import { weatherIconFor } from './kioskWeather/weatherForecastUtils'

const TOPO_SRC = '/kiosk-lobby/topo-map.png'
const LOGO_SRC = '/kiosk-lobby/logo-white.png'

const MAX_PINGS = 6
const SPAWN_EVERY_MS = 2200
const PING_LIFE_MS = 5200

type Ping = {
  id: number
  left: number
  top: number
}

function randomPing(): Omit<Ping, 'id'> {
  // Keep away from corners where brand / clock live.
  return {
    left: 12 + Math.random() * 76,
    top: 10 + Math.random() * 62,
  }
}

export type KioskLobbyAttractProps = {
  className?: string
  /** When true, fills the viewport as a fixed layer (sleep overlay). */
  fixed?: boolean
}

export default function KioskLobbyAttract({ className = '', fixed = false }: KioskLobbyAttractProps) {
  const reactId = useId()
  const [nowMs, setNowMs] = useState(() => Date.now())
  const [pings, setPings] = useState<Ping[]>([])
  const nextIdRef = React.useRef(0)

  const { blocks } = useWeatherForecastBlocks(WALLBOARD_WEATHER_LOCATIONS, { enabled: true })

  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [])

  useEffect(() => {
    const spawn = () => {
      const id = ++nextIdRef.current
      setPings((prev) => {
        const next = [...prev, { id, ...randomPing() }]
        return next.slice(-MAX_PINGS)
      })
      window.setTimeout(() => {
        setPings((prev) => prev.filter((p) => p.id !== id))
      }, PING_LIFE_MS)
    }
    // Seed a couple immediately so the screen isn't empty.
    spawn()
    window.setTimeout(spawn, 700)
    const id = window.setInterval(spawn, SPAWN_EVERY_MS)
    return () => window.clearInterval(id)
  }, [])

  const timeLabel = useMemo(
    () =>
      new Date(nowMs).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
    [nowMs],
  )

  const dateLabel = useMemo(
    () =>
      new Date(nowMs).toLocaleDateString([], {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      }),
    [nowMs],
  )

  const today = useMemo(() => {
    if (!blocks || blocks === 'loading' || blocks.length === 0) return null
    const block = blocks[0]
    if (block.error || !block.days[0]) return null
    return { label: block.label, day: block.days[0] }
  }, [blocks])

  const shellClass = fixed
    ? 'fixed inset-0 z-[10000]'
    : 'absolute inset-0'

  return (
    <div
      className={`kiosk-lobby-attract ${shellClass} overflow-hidden text-white ${className}`}
      aria-label="Larson Tower Specialists lobby display"
    >
      {/* 1. Gradient atmosphere */}
      <div className="kiosk-lobby-attract__gradient pointer-events-none absolute inset-0" aria-hidden />

      {/* 2. Transparent topo overlay (source strokes are black → invert to light lines) */}
      <img
        src={TOPO_SRC}
        alt=""
        aria-hidden
        className="kiosk-lobby-attract__topo pointer-events-none absolute inset-0 h-full w-full object-cover"
        draggable={false}
      />

      {/* Soft vignette so chrome stays readable */}
      <div className="kiosk-lobby-attract__vignette pointer-events-none absolute inset-0" aria-hidden />

      {/* 3. Random map pings */}
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        {pings.map((p) => (
          <span
            key={`${reactId}-${p.id}`}
            className="kiosk-lobby-attract__ping absolute"
            style={{ left: `${p.left}%`, top: `${p.top}%` }}
          >
            <span className="kiosk-lobby-attract__ping-ring" />
            <FontAwesomeIcon icon={faLocationDot} className="kiosk-lobby-attract__ping-icon" />
          </span>
        ))}
      </div>

      {/* 4. Brand + clock / weather */}
      <div className="pointer-events-none absolute inset-0 flex items-end justify-between gap-6 p-8 sm:p-10 md:p-14">
        <div className="kiosk-lobby-attract__brand min-w-0 max-w-[min(52vw,36rem)]">
          <img
            src={LOGO_SRC}
            alt="Larson Tower Specialists"
            className="h-auto w-full max-h-[clamp(3.5rem,14vh,8.5rem)] object-contain object-left drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]"
            draggable={false}
          />
        </div>

        <div className="shrink-0 text-right">
          <div className="text-[clamp(2rem,6vw,4.5rem)] font-semibold tabular-nums leading-none tracking-tight text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]">
            {timeLabel}
          </div>
          <div className="mt-2 text-sm text-white/65 sm:text-base">{dateLabel}</div>
          <div className="mt-3 flex items-center justify-end gap-3 text-white/85">
            {today ? (
              <>
                <FontAwesomeIcon
                  icon={weatherIconFor(today.day.main, today.day.iconCode)}
                  className="text-2xl text-sky-200 sm:text-3xl"
                  title={today.day.main}
                />
                <div className="text-left leading-tight">
                  <div className="text-lg font-semibold tabular-nums sm:text-xl">
                    {today.day.maxF}°
                    <span className="ml-1.5 text-base font-normal text-white/55">{today.day.minF}°</span>
                  </div>
                  <div className="text-xs text-white/55 sm:text-sm">{today.label}</div>
                </div>
              </>
            ) : blocks === 'loading' ? (
              <span className="text-sm text-white/50">Loading weather…</span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
